if (!customElements.get('plp-v2-compare-sidebar')) {
  const SLOT_CACHE_KEY = 'plp-v2-compare-slot-cache';

  class PlpV2CompareSidebar extends HTMLElement {
    connectedCallback() {
      this.maxCompare = Number.parseInt(this.dataset.maxCompare, 10) || 3;
      this.slotsEl = this.querySelector('.js-plp-v2-compare-slots');
      this.compareBtn = this.querySelector('.js-plp-v2-compare-trigger');
      this.compareModal = document.querySelector('.js-compare-modal');
      this.renderGeneration = 0;
      this.deferredRenderId = null;
      this.visibilityObserver = null;
      this.hasRenderedProducts = false;

      this.handleChange = this.handleChange.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleCompareUpdated = this.handleCompareUpdated.bind(this);
      this.handleFacetFiltersUpdated = this.handleFacetFiltersUpdated.bind(this);
      this.render = this.render.bind(this);
      this.runDeferredRender = this.runDeferredRender.bind(this);

      document.addEventListener('change', this.handleChange);
      document.addEventListener('click', this.handleClick);
      document.addEventListener('on:compare:updated', this.handleCompareUpdated);
      document.addEventListener('on:facet-filters:updated', this.handleFacetFiltersUpdated);

      if (this.compareBtn) {
        this.compareBtn.addEventListener('click', this.handleCompareClick.bind(this));
      }

      this.paintSlotsFromCacheOrEmpty();
      this.setupVisibilityObserver();
      this.scheduleDeferredRender();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.handleChange);
      document.removeEventListener('click', this.handleClick);
      document.removeEventListener('on:compare:updated', this.handleCompareUpdated);
      document.removeEventListener('on:facet-filters:updated', this.handleFacetFiltersUpdated);
      this.cancelDeferredRender();
      this.visibilityObserver?.disconnect();
    }

    static getSlotCache() {
      try {
        return JSON.parse(sessionStorage.getItem(SLOT_CACHE_KEY)) || {};
      } catch (error) {
        return {};
      }
    }

    static setSlotCache(cache) {
      try {
        sessionStorage.setItem(SLOT_CACHE_KEY, JSON.stringify(cache));
      } catch (error) {
        // sessionStorage may be unavailable or full
      }
    }

    getCachedSlotHtml(productId) {
      const cache = PlpV2CompareSidebar.getSlotCache();
      const entry = cache[String(productId)];
      return entry?.html || null;
    }

    setCachedSlotHtml(productId, html) {
      const cache = PlpV2CompareSidebar.getSlotCache();
      cache[String(productId)] = { html };
      PlpV2CompareSidebar.setSlotCache(cache);
    }

    removeCachedSlotHtml(productId) {
      const cache = PlpV2CompareSidebar.getSlotCache();
      delete cache[String(productId)];
      PlpV2CompareSidebar.setSlotCache(cache);
    }

    setupVisibilityObserver() {
      if (!('IntersectionObserver' in window)) return;

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.scheduleDeferredRender();
        },
        { rootMargin: '100px' }
      );

      this.visibilityObserver.observe(this);
    }

    cancelDeferredRender() {
      if (this.deferredRenderId === null) return;

      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(this.deferredRenderId);
      } else {
        window.clearTimeout(this.deferredRenderId);
      }

      this.deferredRenderId = null;
    }

    scheduleDeferredRender() {
      if (typeof CompareUtil === 'undefined' || this.getCompareCount() === 0) {
        this.updateButtonState();
        return;
      }

      if (this.deferredRenderId !== null) return;

      const run = () => {
        this.deferredRenderId = null;
        this.runDeferredRender();
      };

      if (typeof window.requestIdleCallback === 'function') {
        this.deferredRenderId = window.requestIdleCallback(run, { timeout: 2000 });
      } else {
        this.deferredRenderId = window.setTimeout(run, 150);
      }
    }

    runDeferredRender() {
      if (!this.isVisible()) return;
      this.render({ deferred: true });
    }

    isVisible() {
      return this.offsetParent !== null || this.getClientRects().length > 0;
    }

    getCompareProducts() {
      if (typeof CompareUtil !== 'undefined') {
        return CompareUtil.getSelectedProducts();
      }

      if (typeof theme !== 'undefined' && theme.storageUtil) {
        return theme.storageUtil.get('compare-products', true) || [];
      }

      return [];
    }

    getCompareCount() {
      return this.getCompareProducts().length;
    }

    handleChange(evt) {
      if (!evt.target.classList.contains('js-compare-checkbox')) return;

      window.requestAnimationFrame(() => {
        this.paintSlotsFromCacheOrEmpty();
        this.render({ immediate: true });
      });
    }

    handleCompareUpdated() {
      this.paintSlotsFromCacheOrEmpty();

      if (this.hasRenderedProducts) {
        this.render({ immediate: true });
      } else {
        this.scheduleDeferredRender();
      }
    }

    handleFacetFiltersUpdated() {
      this.paintSlotsFromCacheOrEmpty();
      this.scheduleDeferredRender();
    }

    handleClick(evt) {
      const removeBtn = evt.target.closest('.js-plp-v2-compare-remove');
      if (!removeBtn || !this.contains(removeBtn)) return;

      evt.preventDefault();
      if (typeof CompareUtil === 'undefined') return;

      const productId = removeBtn.dataset.productId.toString();
      CompareUtil.removeFromCompare(productId, true);
      this.removeCachedSlotHtml(productId);
      this.paintSlotsFromCacheOrEmpty();
      this.render({ immediate: true });
    }

    handleCompareClick() {
      if (typeof CompareUtil === 'undefined') return;
      if (CompareUtil.getCompareCount() < 2 || !this.compareModal) return;

      this.compareModal.dispatchEvent(
        new CustomEvent('on:product-compare-modal:open', { bubbles: true })
      );
    }

    emptySlotHtml() {
      return '<div class="plp-v2-compare-slot plp-v2-compare-slot--empty" role="listitem" aria-hidden="true"><span class="plp-v2-compare-slot__plus" aria-hidden="true">+</span></div>';
    }

    wrapSlotHtml(html) {
      return `<div class="plp-v2-compare-slot" role="listitem">${html}</div>`;
    }

    paintSlotsFromCacheOrEmpty() {
      if (!this.slotsEl) {
        return;
      }

      const compareProducts = this.getCompareProducts();
      const slotHtml = [];

      for (let i = 0; i < this.maxCompare; i += 1) {
        const product = compareProducts[i];
        if (!product) {
          slotHtml.push(this.emptySlotHtml());
          continue;
        }

        const cached = this.getCachedSlotHtml(product.id);
        slotHtml.push(cached || this.emptySlotHtml());
      }

      this.slotsEl.innerHTML = slotHtml.join('');
      this.updateButtonState();
    }

    paintEmptySlots() {
      if (!this.slotsEl) return;

      this.slotsEl.innerHTML = Array.from({ length: this.maxCompare }, () => this.emptySlotHtml()).join('');
      this.updateButtonState();
    }

    updateButtonState() {
      if (!this.compareBtn) return;

      const compareCount = this.getCompareCount();
      const canCompare = compareCount >= 2;
      this.compareBtn.disabled = !canCompare;
      this.compareBtn.setAttribute('aria-disabled', canCompare ? 'false' : 'true');
    }

    async fetchSlot(product, generation) {
      if (typeof CompareUtil === 'undefined' || generation !== this.renderGeneration) {
        return this.emptySlotHtml();
      }

      const cached = this.getCachedSlotHtml(product.id);
      if (cached) return cached;

      try {
        const response = await fetch(
          CompareUtil.getSectionRenderUrl(product.url, 'product-compare-plp-slot')
        );

        if (!response.ok || generation !== this.renderGeneration) {
          return cached || this.emptySlotHtml();
        }

        const data = await response.json();
        const html = data['product-compare-plp-slot'];

        if (!html || generation !== this.renderGeneration) {
          return cached || this.emptySlotHtml();
        }

        const wrapped = this.wrapSlotHtml(html);
        this.setCachedSlotHtml(product.id, wrapped);
        return wrapped;
      } catch (error) {
        return cached || this.emptySlotHtml();
      }
    }

    async render({ immediate = false, deferred = false } = {}) {
      if (!this.slotsEl || typeof CompareUtil === 'undefined') return;

      if (deferred && !this.isVisible()) {
        this.scheduleDeferredRender();
        return;
      }

      if (!immediate && deferred && this.hasRenderedProducts) {
        const compareProducts = this.getCompareProducts();
        const needsFetch = compareProducts.some((product) => !this.getCachedSlotHtml(product.id));
        if (!needsFetch) return;
      }

      this.cancelDeferredRender();
      const generation = this.renderGeneration + 1;
      this.renderGeneration = generation;

      const compareProducts = this.getCompareProducts();

      if (compareProducts.length === 0) {
        this.paintEmptySlots();
        this.hasRenderedProducts = false;
        return;
      }

      const slotPromises = Array.from({ length: this.maxCompare }, (_, index) => {
        const product = compareProducts[index];
        return product ? this.fetchSlot(product, generation) : Promise.resolve(this.emptySlotHtml());
      });

      const slotHtml = await Promise.all(slotPromises);

      if (generation !== this.renderGeneration) return;

      this.slotsEl.innerHTML = slotHtml.join('');
      this.updateButtonState();
      this.hasRenderedProducts = true;
    }
  }

  customElements.define('plp-v2-compare-sidebar', PlpV2CompareSidebar);
}
